import React, { useState } from 'react';
import { Form, Button, Modal, Nav, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  signInWithEmailAndPassword, 
  setPersistence, 
  browserSessionPersistence 
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  setDoc, 
  updateDoc 
} from 'firebase/firestore';
import './AuthForms.css';

const AuthForms = ({ show, handleClose }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [role, setRole] = useState('student');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    rollNumber: '',
    teacherId: '',
    childRollNumber: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      rollNumber: '',
      teacherId: '',
      childRollNumber: ''
    });
  };

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    clearMessages();
    resetForm();
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    clearMessages();
  };

  const validateStudentSignup = async (firestore) => {
    if (!formData.rollNumber) {
      throw new Error('Roll Number is required for student registration.');
    }

    // Check admissions collection with rollNumber field
    const admissionsRef = collection(firestore, 'admissions');
    const q = query(admissionsRef, where('rollNumber', '==', formData.rollNumber));
    const studentQuerySnapshot = await getDocs(q);
    
    if (studentQuerySnapshot.empty) {
      throw new Error('Roll Number not found in our records. Please check your Roll Number or contact school administration.');
    }
    
    const studentDoc = studentQuerySnapshot.docs[0];
    const studentData = studentDoc.data();
    
    // Check if name matches
    if (formData.name.toLowerCase() !== studentData.studentName.toLowerCase()) {
      throw new Error('Full name does not match our records for this Roll Number. Please enter your name exactly as per school records.');
    }

    // Check if already registered
    if (studentData.registeredEmail && studentData.registeredEmail.trim() !== '') {
      throw new Error('This Roll Number is already registered with another email address. Please login instead.');
    }

    return { studentDoc, studentData };
  };

  const validateTeacherSignup = async (firestore) => {
    if (!formData.teacherId) {
      throw new Error('Teacher ID is required for teacher registration.');
    }

    // Check teachers collection if available
    try {
      const teachersRef = collection(firestore, 'teachers');
      const q = query(teachersRef, where('teacherId', '==', formData.teacherId));
      const teacherSnapshot = await getDocs(q);
      
      if (!teacherSnapshot.empty) {
        const teacherData = teacherSnapshot.docs[0].data();
        if (formData.name.toLowerCase() !== teacherData.name.toLowerCase()) {
          throw new Error('Full name does not match our records for this Teacher ID.');
        }
      }
    } catch (error) {
      console.log('Teachers collection not available for verification');
      // Continue with signup even if teachers collection doesn't exist
    }
  };

  const validateParentSignup = async (firestore) => {
    if (!formData.childRollNumber) {
      throw new Error('Child Roll Number is required for parent registration.');
    }

    // Verify child exists in admissions using rollNumber
    const admissionsRef = collection(firestore, 'admissions');
    const q = query(admissionsRef, where('rollNumber', '==', formData.childRollNumber));
    const childSnapshot = await getDocs(q);
    
    if (childSnapshot.empty) {
      throw new Error('Child Roll Number not found. Please check the Roll Number or contact school administration.');
    }
  };

  const handleSignup = async (auth, firestore) => {
    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      throw new Error('Passwords do not match. Please ensure both password fields are identical.');
    }

    if (formData.password.length < 6) {
      throw new Error('Password must be at least 6 characters long for security.');
    }

    // Role-specific validation
    if (role === 'student') {
      await validateStudentSignup(firestore);
    } else if (role === 'teacher') {
      await validateTeacherSignup(firestore);
    } else if (role === 'parent') {
      await validateParentSignup(firestore);
    }

    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
    
    // Prepare user data for database
    const userData = {
      email: formData.email.toLowerCase(),
      name: formData.name,
      role: role,
      createdAt: new Date(),
      emailVerified: false
    };

    // Add role-specific fields
    if (role === 'student') {
      const admissionsRef = collection(firestore, 'admissions');
      const q = query(admissionsRef, where('rollNumber', '==', formData.rollNumber));
      const studentSnapshot = await getDocs(q);
      
      if (!studentSnapshot.empty) {
        const studentDoc = studentSnapshot.docs[0];
        userData.rollNumber = formData.rollNumber;
        userData.admissionId = studentDoc.id;

        // Update admissions record
        try {
          const admissionDocRef = doc(firestore, 'admissions', studentDoc.id);
          await updateDoc(admissionDocRef, {
            registeredEmail: formData.email.toLowerCase(),
            registered: true,
            registrationDate: new Date(),
            userId: userCredential.user.uid
          });
        } catch (updateError) {
          console.log('Admissions record update not required');
        }
      }
    } else if (role === 'teacher') {
      userData.teacherId = formData.teacherId;
    } else if (role === 'parent') {
      userData.childRollNumber = formData.childRollNumber;
    }

    // Store user data in database
    const userRef = doc(firestore, 'users', userCredential.user.uid);
    await setDoc(userRef, userData);

    // Send verification email
    await sendEmailVerification(userCredential.user);
    
    return 'Account created successfully! We have sent a verification email to your email address. Please check your inbox (and spam folder) and click the verification link before logging in.';
  };

  const handleLogin = async (auth, firestore) => {
    await setPersistence(auth, browserSessionPersistence);
    const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
    
    // Check email verification
    if (!userCredential.user.emailVerified) {
      await auth.signOut();
      throw new Error('Please verify your email address before logging in. We have sent a verification email to your inbox. Please check your spam folder if you cannot find it.');
    }

    // Get user data from database
    const userRef = doc(firestore, 'users', userCredential.user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await auth.signOut();
      throw new Error('Your account profile was not found in our system. Please contact school administration for assistance.');
    }

    const userData = userDoc.data();
    
    // Verify role matches
    if (role !== userData.role) {
      await auth.signOut();
      throw new Error(`Your account is registered as ${userData.role} in our system. Please select "${userData.role}" from the role dropdown to login.`);
    }

    return userData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const firestore = getFirestore();
    
    clearMessages();
    setIsLoading(true);

    try {
      if (activeTab === 'signup') {
        const message = await handleSignup(auth, firestore);
        setSuccessMessage(message);
        resetForm();
      } else {
        const userData = await handleLogin(auth, firestore);
        handleClose();
        
        // Redirect based on role
        switch (userData.role) {
          case 'student':
            navigate('/StudentDashboard', { 
              state: { 
                email: formData.email,
                role: 'student'
              }
            });
            break;
          case 'teacher':
            navigate('/TeacherDashboard');
            break;
          case 'parent':
            navigate('/ParentDashboard');
            break;
          case 'admin':
            navigate('/AdminDashboard');
            break;
          default:
            setError('We encountered an issue with your account role. Please contact support.');
        }
      }
    } catch (error) {
      console.log('Authentication process error:', error);
      
      // Handle specific error cases with custom messages
      if (error.message.includes('email-already-in-use')) {
        setError('This email address is already registered in our system. Please use a different email address or login with your existing account.');
      } else if (error.message.includes('user-not-found')) {
        setError('No account found with this email address. Please check your email or create a new account.');
      } else if (error.message.includes('wrong-password')) {
        setError('The password you entered is incorrect. Please try again or use the forgot password option.');
      } else if (error.message.includes('network-request-failed')) {
        setError('Network connection issue. Please check your internet connection and try again.');
      } else if (error.message.includes('invalid-credential') || error.message.includes('invalid-email')) {
        setError('The email or password you entered is invalid. Please check your credentials and try again.');
      } else if (error.message.includes('permission-denied')) {
        setError('System access temporarily unavailable. Please try again in a few moments.');
      } else {
        setError(error.message || 'We encountered an unexpected issue. Please try again or contact school administration if the problem continues.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center">
          <h4 className="mb-0">{activeTab === 'login' ? 'School Portal Login' : 'Create School Portal Account'}</h4>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Nav variant="pills" className="mb-4 justify-content-center">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'login'} 
              onClick={() => handleTabChange('login')}
              className="mx-2"
            >
              Login
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'signup'} 
              onClick={() => handleTabChange('signup')}
              className="mx-2"
            >
              Sign Up
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert variant="success" className="mb-3">
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {activeTab === 'signup' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Full Name</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name as per school records"
                className="py-2"
              />
              <Form.Text className="text-muted">
                Must match exactly with your school registration records
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              <strong>I am a</strong> <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="role"
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
              required
              className="py-2"
            >
              <option value="student">Student</option>
              <option value="parent">Parent</option>
              <option value="teacher">Teacher</option>
              {activeTab === 'login' && <option value="admin">Administrator</option>}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Email Address</strong> <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your active email address"
              className="py-2"
            />
            {activeTab === 'signup' && (
              <Form.Text className="text-muted">
                Use a valid email address you have access to
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Password</strong> <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="Create a secure password"
              className="py-2"
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters long
            </Form.Text>
          </Form.Group>

          {activeTab === 'signup' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Confirm Password</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength="6"
                placeholder="Re-enter your password for confirmation"
                className="py-2"
              />
            </Form.Group>
          )}

          {role === 'student' && activeTab === 'signup' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Roll Number</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="rollNumber"
                value={formData.rollNumber}
                onChange={handleChange}
                required
                placeholder="Enter your school Roll Number"
                className="py-2"
              />
              <Form.Text className="text-muted">
                This Roll Number was provided during your school admission
              </Form.Text>
            </Form.Group>
          )}

          {role === 'parent' && activeTab === 'signup' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Child's Roll Number</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="childRollNumber"
                value={formData.childRollNumber}
                onChange={handleChange}
                required
                placeholder="Enter your child's Roll Number"
                className="py-2"
              />
              <Form.Text className="text-muted">
                Enter the Roll Number of your child studying in our school
              </Form.Text>
            </Form.Group>
          )}

          {role === 'teacher' && activeTab === 'signup' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Teacher ID</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="teacherId"
                value={formData.teacherId}
                onChange={handleChange}
                required
                placeholder="Enter your assigned Teacher ID"
                className="py-2"
              />
              <Form.Text className="text-muted">
                Your unique Teacher ID provided by school administration
              </Form.Text>
            </Form.Group>
          )}

          <div className="d-grid gap-2 mt-4">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isLoading}
              size="lg"
              className="py-2"
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                activeTab === 'login' ? 'Login to Portal' : 'Create Account'
              )}
            </Button>
            
            <Button 
              variant="outline-secondary" 
              onClick={handleClose} 
              disabled={isLoading}
              className="py-2"
            >
              Cancel
            </Button>
          </div>

          {activeTab === 'login' && (
            <div className="text-center mt-3">
              <small className="text-muted">
                Don't have an account?{' '}
                <Button 
                  variant="link" 
                  className="p-0 align-baseline" 
                  onClick={() => handleTabChange('signup')}
                >
                  Sign up here
                </Button>
              </small>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AuthForms;